import apiClient from '../utils/axios-config';
import { OutfitMetadata } from '../types';
import { ImageCompressionUtils } from '../utils/imageCompressionUtils';

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev' || 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

interface UploadUrlResponse {
  uploadUrl: string;
  key: string;
  expiresIn: string;
}

/**
 * Content moderation error thrown when the backend rejects an upload
 * due to content policy violations.
 */
export class ContentModerationError extends Error {
  public moderationAction: 'WARN' | 'FINAL_WARN' | 'BLOCK' | 'PERMANENT_BLOCK';
  public warningMessage: string;
  public violationCount: number;
  public blocked: boolean;
  public isPermanent: boolean;
  public blockExpiresAt?: string;
  public remainingHours?: number;

  constructor(data: {
    message: string;
    moderationAction: string;
    warningMessage: string;
    violationCount: number;
    blocked?: boolean;
    isPermanent?: boolean;
    blockExpiresAt?: string;
    expiresAt?: string;
    remainingHours?: number;
  }) {
    super(data.message);
    this.name = 'ContentModerationError';
    this.moderationAction = data.moderationAction as any;
    this.warningMessage = data.warningMessage || data.message;
    this.violationCount = data.violationCount || 0;
    this.blocked = data.blocked || false;
    this.isPermanent = data.isPermanent || false;
    this.blockExpiresAt = data.blockExpiresAt || data.expiresAt;
    this.remainingHours = data.remainingHours;
  }
}

/**
 * Upload blocked error thrown when the user is blocked from uploading.
 */
export class UploadBlockedError extends Error {
  public isPermanent: boolean;
  public violationCount: number;
  public expiresAt?: string;
  public remainingHours?: number;

  constructor(data: {
    message: string;
    isPermanent?: boolean;
    violationCount?: number;
    expiresAt?: string;
    remainingHours?: number;
  }) {
    super(data.message);
    this.name = 'UploadBlockedError';
    this.isPermanent = data.isPermanent || false;
    this.violationCount = data.violationCount || 0;
    this.expiresAt = data.expiresAt;
    this.remainingHours = data.remainingHours;
  }
}

class UploadService {
  /**
   * Get a presigned URL for uploading a file
   */
  async getUploadUrl(fileName: string, contentType: string, fileSize: number): Promise<UploadUrlResponse> {
    try {
      // Let the axios interceptor handle auth headers automatically
      const response = await apiClient.post(`${API_BASE_URL}/upload/url`, {
        fileName,
        contentType,
        fileSize,
      }, {
        timeout: 30000, // 30 second timeout
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error getting upload URL:', error);
      throw error;
    }
  }

  /**
   * Upload a file directly to S3 using a presigned URL
   */
  async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    try {
      // Use axios for S3 uploads — the interceptor already skips auth for S3 URLs
      // Set a generous timeout for mobile uploads on slow connections
      await apiClient.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        timeout: 120000, // 2 minute timeout for large mobile photos
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
    } catch (error: any) {
      console.error('Error uploading file to S3:', error);
      if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out. Please check your connection and try again.');
      }
      throw error;
    }
  }

  /**
   * Create an outfit record after the file has been uploaded to S3
   * @param onBehalfOfUserId Optional user ID for admin upload-on-behalf
   */
  async createOutfitRecord(imageUrl: string, thumbnailUrl: string, metadata: OutfitMetadata, onBehalfOfUserId?: string) {
    try {
      // Let the axios interceptor handle auth headers automatically
      const response = await apiClient.post(`${API_BASE_URL}/outfits`, {
        imageUrl,
        thumbnailUrl,
        metadata,
        ...(onBehalfOfUserId ? { onBehalfOfUserId } : {}),
        ratings: {
          averagerating: 0,
          totalratingsCount: 0,
        },
        likes: 0,
      }, {
        timeout: 30000,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating outfit record:', error);

      // Parse moderation-specific error responses from the backend
      const responseData = error?.response?.data;
      const statusCode = error?.response?.status;

      if (responseData) {
        // Check for upload blocked (403 with UPLOAD_BLOCKED code)
        if (statusCode === 403 && responseData.error?.code === 'UPLOAD_BLOCKED') {
          throw new UploadBlockedError({
            message: responseData.error.message || 'Your upload privileges have been suspended.',
            isPermanent: responseData.isPermanent,
            violationCount: responseData.violationCount,
            expiresAt: responseData.expiresAt,
            remainingHours: responseData.remainingHours,
          });
        }

        // Check for content moderation failure (400 or 403 with CONTENT_MODERATION_FAILED)
        if (responseData.error?.code === 'CONTENT_MODERATION_FAILED' || responseData.moderationAction) {
          throw new ContentModerationError({
            message: responseData.error?.message || responseData.message || 'Content moderation failed.',
            moderationAction: responseData.moderationAction || 'WARN',
            warningMessage: responseData.warningMessage || responseData.error?.message || '',
            violationCount: responseData.violationCount || 0,
            blocked: responseData.blocked,
            isPermanent: responseData.isPermanent,
            blockExpiresAt: responseData.blockExpiresAt,
            expiresAt: responseData.expiresAt,
            remainingHours: responseData.remainingHours,
          });
        }

        // Check for moderation unavailable (503)
        if (statusCode === 503 && responseData.error?.code === 'MODERATION_UNAVAILABLE') {
          throw new Error(responseData.error.message || 'Content moderation is temporarily unavailable. Please try again.');
        }
      }

      throw error;
    }
  }

  /**
   * Complete upload flow: compress image, get presigned URL, upload to S3, create outfit record
   */
  async uploadOutfit(file: File, metadata: OutfitMetadata, onProgress?: (stepId: string) => void) {
    try {
      console.log('[UploadService] Starting upload process', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      // Step 1: Check if image needs compression and compress if necessary
      let fileToUpload = file;
      let compressionInfo = null;

      onProgress?.('compress');
      if (ImageCompressionUtils.needsCompression(file)) {
        console.log('[UploadService] Image needs compression, compressing...');
        
        try {
          const optimalSettings = ImageCompressionUtils.getOptimalSettings(file);
          const compressionResult = await ImageCompressionUtils.compressImage(file, optimalSettings);
          
          fileToUpload = compressionResult.compressedFile;
          compressionInfo = {
            originalSize: compressionResult.originalSize,
            compressedSize: compressionResult.compressedSize,
            compressionRatio: compressionResult.compressionRatio,
            dimensions: compressionResult.dimensions,
          };

          console.log('[UploadService] Image compressed successfully', compressionInfo);
        } catch (compressionError) {
          console.warn('[UploadService] Image compression failed, uploading original', compressionError);
          // Continue with original file if compression fails
        }
      } else {
        console.log('[UploadService] Image size is acceptable, no compression needed');
      }

      // Step 2: Get presigned URL and upload to S3
      onProgress?.('upload');
      const uploadResponse = await this.getUploadUrl(
        fileToUpload.name,
        fileToUpload.type,
        fileToUpload.size
      );
      
      console.log('[UploadService] Upload URL response:', uploadResponse);
      const { uploadUrl, key } = uploadResponse;

      // Step 3: Upload file to S3
      await this.uploadFileToS3(uploadUrl, fileToUpload);

      // Step 4: Construct the image URLs
      const cloudFrontDomain = process.env.REACT_APP_IMAGES_URL?.replace('https://', '') || 'd34q17au2ltgc3.cloudfront.net';
      const imageUrl = `https://${cloudFrontDomain}/${key}`;
      const thumbnailUrl = imageUrl; // For now, use the same URL

      // Step 5: Create outfit record — backend does moderation, AI analysis, indexing
      // Signal that backend processing is starting
      onProgress?.('backend_start');
      const outfitMetadata = {
        ...metadata,
        compressionInfo,
        uploadInfo: {
          originalFileName: file.name,
          uploadedFileName: fileToUpload.name,
          originalSize: file.size,
          uploadedSize: fileToUpload.size,
          uploadedAt: new Date().toISOString(),
        },
      };

      const outfit = await this.createOutfitRecord(imageUrl, thumbnailUrl, outfitMetadata);

      console.log('[UploadService] Upload completed successfully', {
        outfitId: outfit.id,
        imageUrl,
        compressionInfo,
      });

      return outfit;
    } catch (error) {
      console.error('Error in complete upload flow:', error);
      throw error;
    }
  }

  /**
   * Upload an outfit on behalf of another user (admin only).
   * Uses the same full pipeline as uploadOutfit but passes onBehalfOfUserId
   * to the backend so the outfit is attributed to the target user.
   */
  async uploadOutfitOnBehalf(file: File, metadata: OutfitMetadata, onBehalfOfUserId: string, onProgress?: (stepId: string) => void) {
    try {
      console.log('[UploadService] Starting upload-on-behalf process', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        onBehalfOfUserId,
      });

      // Step 1: Check if image needs compression and compress if necessary
      let fileToUpload = file;
      let compressionInfo = null;

      onProgress?.('compress');
      if (ImageCompressionUtils.needsCompression(file)) {
        console.log('[UploadService] Image needs compression, compressing...');
        
        try {
          const optimalSettings = ImageCompressionUtils.getOptimalSettings(file);
          const compressionResult = await ImageCompressionUtils.compressImage(file, optimalSettings);
          
          fileToUpload = compressionResult.compressedFile;
          compressionInfo = {
            originalSize: compressionResult.originalSize,
            compressedSize: compressionResult.compressedSize,
            compressionRatio: compressionResult.compressionRatio,
            dimensions: compressionResult.dimensions,
          };

          console.log('[UploadService] Image compressed successfully', compressionInfo);
        } catch (compressionError) {
          console.warn('[UploadService] Image compression failed, uploading original', compressionError);
        }
      } else {
        console.log('[UploadService] Image size is acceptable, no compression needed');
      }

      // Step 2: Get presigned URL
      onProgress?.('upload');
      const uploadResponse = await this.getUploadUrl(
        fileToUpload.name,
        fileToUpload.type,
        fileToUpload.size
      );
      
      const { uploadUrl, key } = uploadResponse;

      // Step 3: Upload file to S3
      await this.uploadFileToS3(uploadUrl, fileToUpload);

      // Step 4: Construct the image URLs
      const cloudFrontDomain = process.env.REACT_APP_IMAGES_URL?.replace('https://', '') || 'd34q17au2ltgc3.cloudfront.net';
      const imageUrl = `https://${cloudFrontDomain}/${key}`;
      const thumbnailUrl = imageUrl;

      // Step 5: Create outfit record with onBehalfOfUserId — backend does moderation, AI, indexing
      onProgress?.('backend_start');
      const outfitMetadata = {
        ...metadata,
        compressionInfo,
        uploadInfo: {
          originalFileName: file.name,
          uploadedFileName: fileToUpload.name,
          originalSize: file.size,
          uploadedSize: fileToUpload.size,
          uploadedAt: new Date().toISOString(),
        },
      };

      const outfit = await this.createOutfitRecord(imageUrl, thumbnailUrl, outfitMetadata, onBehalfOfUserId);

      console.log('[UploadService] Upload-on-behalf completed successfully', {
        outfitId: outfit.id,
        imageUrl,
        onBehalfOfUserId,
      });

      return outfit;
    } catch (error) {
      console.error('Error in upload-on-behalf flow:', error);
      throw error;
    }
  }

  /**
   * Request backend compression for already uploaded images
   */
  async requestImageCompression(imageKey: string, createVariants: boolean = false) {
    try {
      const response = await apiClient.post(`${API_BASE_URL}/upload/compress`, {
        imageKey,
        createVariants,
        compressionOptions: {
          maxSizeBytes: 2 * 1024 * 1024, // 2MB
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 85,
          format: 'jpeg',
        },
      }, {
        timeout: 30000,
      });

      return response.data;
    } catch (error) {
      console.error('Error requesting image compression:', error);
      throw error;
    }
  }
}

export default new UploadService();